import { clsx } from "clsx";
import { useState } from "react";

import { ABLLSTask } from "@api/assessments/assessment-schema-types";

interface FillBarProps {
  task: ABLLSTask;
  sessions: { Id: number }[];
  currentSessionId: number;
  onScore: (newScore: number | null) => void;
}

export const FillBar: React.FC<FillBarProps> = ({ task, sessions, currentSessionId, onScore }) => {
  const max = task["Max Score"];
  const currentIdx = sessions.findIndex((s) => s.Id === currentSessionId);
  const currentEntry = task.Scores.find((s) => s.SessionId === currentSessionId);
  const currentScore = currentEntry?.Score ?? 0;
  const prevSessionScore = sessions
    .slice(0, currentIdx)
    .reduce((acc, s) => Math.max(acc, task.Scores.find((sc) => sc.SessionId === s.Id)?.Score ?? 0), 0);

  // The first administration scored 0 stays marked, in that session's colour,
  // even after the learner later scores above 0. Independent of the selected
  // session, which only sets the edit-pen colour.
  const zeroSessionId =
    task.Scores.filter((s) => s.Score === 0 && s.Assessed !== false).sort(
      (a, b) => sessions.findIndex((x) => x.Id === a.SessionId) - sessions.findIndex((x) => x.Id === b.SessionId),
    )[0]?.SessionId ?? null;
  const isTestedZero = zeroSessionId !== null;
  const currentIsZero = currentEntry?.Score === 0 && currentEntry?.Assessed !== false;

  const [previewIdx, setPreviewIdx] = useState<number | null>(null);
  const previewScore = previewIdx !== null ? (previewIdx === currentScore ? previewIdx - 1 : previewIdx) : null;

  return (
    <div className="flex items-center gap-3">
      <button
        type="button"
        aria-label="Tested, scored 0"
        aria-pressed={isTestedZero}
        className="w-3 h-3 rounded-full border-2 shrink-0 transition-colors hover:opacity-80 disabled:cursor-not-allowed"
        style={{
          borderColor: isTestedZero ? `var(--chart-${zeroSessionId})` : "#ddd",
          backgroundColor: isTestedZero ? `var(--chart-${zeroSessionId})` : "white",
        }}
        disabled={!!prevSessionScore || currentScore > 0}
        onClick={() => onScore(currentIsZero ? null : 0)}
      />
      <div className="flex gap-1 flex-1">
        {Array.from({ length: max }, (_, idx) => {
          const i = idx + 1;
          const isInCurrentRange = i > prevSessionScore;
          const owner = task.Scores.filter((v) => v.Score > idx).sort(
            (a, b) =>
              sessions.findIndex((el) => el.Id === a.SessionId) - sessions.findIndex((el) => el.Id === b.SessionId),
          )[0];

          const isOwnedByCurrentSession = owner?.SessionId === currentSessionId;
          const ownerSessionIdx = owner ? sessions.findIndex((s) => s.Id === owner.SessionId) : -1;
          const inPreviewFill =
            previewScore !== null &&
            i > prevSessionScore &&
            i <= previewScore &&
            (ownerSessionIdx === -1 || ownerSessionIdx > currentIdx);
          const inPreviewClear =
            previewScore !== null && i > prevSessionScore && i > previewScore && isOwnedByCurrentSession;

          return (
            <button
              key={i}
              type="button"
              className={clsx(
                "h-6 flex-1 border rounded transition disabled:cursor-not-allowed hover:cursor-pointer bg-white disabled:opacity-50",
                inPreviewClear && "opacity-50",
              )}
              style={{
                backgroundColor: owner ? `var(--chart-${owner.SessionId})` : undefined,
                boxShadow: inPreviewFill ? `inset 0 0 0 3px var(--chart-${currentSessionId})` : undefined,
              }}
              disabled={!isInCurrentRange}
              onMouseEnter={() => {
                if (isInCurrentRange) setPreviewIdx(i);
              }}
              onMouseLeave={() => setPreviewIdx(null)}
              onFocus={() => {
                if (isInCurrentRange) setPreviewIdx(i);
              }}
              onBlur={() => setPreviewIdx(null)}
              onClick={() => {
                if (!isInCurrentRange) return;
                setPreviewIdx(null);
                const newVal = i === currentScore ? i - 1 : i;
                onScore(newVal <= prevSessionScore ? null : newVal);
              }}
            />
          );
        })}
      </div>
    </div>
  );
};
